import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'apps/portfolio-performance/portfolio-utils';
import { RichTextEditor } from '@evertrue/sdk';
import { Icon, Button } from '@evertrue/et-components';
import useFileUploader from 'components/emailer/hooks/use-file-uploader';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';
import { ORG_TAGS_QUERY, REGISTER_TAG_MUTATION } from 'apps/cadences-v2/utils/cadence-queries';

const EmailSignaturesForm = ({ selectedSignature = {}, updateSignature, isSaved, setIsSaved, contact }) => {
  const { signature_body, id, tags: existingTags } = selectedSignature;
  const { flagEnabled: signalEnabled } = useFeatureFlag('signal');
  const { flagEnabled: cadenceV2Enabled } = useFeatureFlag('cadenceV2Internal');
  const hasCadenceV2 = signalEnabled && cadenceV2Enabled;
  const [body, setSignatureBody] = useState(signature_body);
  const [tags, setTags] = useState(existingTags || []);
  const [isEdited, setIsEdited] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const debouncedBody = useDebounce(body, 1000);

  const { onImageUpload } = useFileUploader([], null);

  // Keep track of the previous signature ID to detect signature changes
  const prevSignatureIdRef = useRef(id);
  const joditRef = useRef(null);

  /**
   * loads the selected signature into the editor when a new signature is selected.
   * - Detects signature changes using the ID
   * - Resets all state variables to prevent auto-save on signature switches
   */
  useEffect(() => {
    const isSignatureChanged = prevSignatureIdRef.current !== id;

    if (isSignatureChanged) {
      // Update body and reset edit state when signature changes
      setSignatureBody(signature_body);
      setTags(existingTags || []);
      document.getElementsByClassName('jodit-wysiwyg')[1].innerHTML = signature_body;
      setIsEdited(false);
      setIsSaved(true);

      // Update the previous signature ID reference
      prevSignatureIdRef.current = id;
    }
  }, [id, signature_body, existingTags, setIsSaved]);

  /**
   * auto-saves the signature after user has stopped typing for 1 second.
   * - Runs only when the user has explicitly made edits
   * - Uses the debounced body value for the actual save
   */
  useEffect(() => {
    // Skip if we're not in edit mode or if the signature ID has changed
    if (!isEdited || prevSignatureIdRef.current !== id) {
      return;
    }

    // Only save if the debounced content is different from the original
    if (debouncedBody !== signature_body) {
      const updateSignatureAsync = async () => {
        setIsSaved(false);
        await updateSignature(id, { signature_body: debouncedBody, tags });
        setIsSaved(true);
      };
      updateSignatureAsync();
    }
  }, [debouncedBody, id, signature_body, tags, updateSignature, isEdited, setIsSaved]);

  useEffect(() => {
    if (!tagInput.trim()) {
      setTagSuggestions([]);
      return;
    }
    let stale = false;
    const timer = setTimeout(() => {
      cadenceGqlCall(ORG_TAGS_QUERY, { search: tagInput.trim() })
        .then((data) => {
          if (!stale) setTagSuggestions(data?.orgTags || []);
        })
        .catch(() => {
          if (!stale) setTagSuggestions([]);
        });
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [tagInput]);

  // Handles user input in the text editor.
  const handleTextChange = (newBody) => {
    // Only set edited flag if the content actually changed
    if (newBody !== body) {
      setSignatureBody(newBody);
      setIsSaved(false);
      setIsEdited(true);
    }
  };

  const handleAddTag = (tagValue) => {
    const val = tagValue.trim().toLowerCase();
    if (val && !tags.map((t) => t.toLowerCase()).includes(val)) {
      const newTags = [...tags, val];
      setTags(newTags);
      setIsSaved(false);
      cadenceGqlCall(REGISTER_TAG_MUTATION, { name: val }).catch((e) => {
        console.warn('[EmailSignature] Failed to register tag in org registry:', e?.message);
      });
      updateSignature(id, { signature_body: body, tags: newTags }).then(() => setIsSaved(true));
    }
    setTagInput('');
    setTagSuggestions([]);
  };

  const handleRemoveTag = (tag) => {
    const newTags = tags.filter((t) => t !== tag);
    setTags(newTags);
    setIsSaved(false);
    updateSignature(id, { signature_body: body, tags: newTags }).then(() => setIsSaved(true));
  };

  return (
    <div className="email-signature-form">
      <p>
        <strong>Customize Your Signature</strong>
      </p>
      <div>
        <RichTextEditor
          ref={joditRef}
          placeholder="Add Signature..."
          value={body}
          onChange={handleTextChange}
          onImageUpload={onImageUpload}
        />
        <div className="email-signature-body-footer">
          <div>
            {isSaved ? (
              <span className="saved-text">&#x2713; Saved</span>
            ) : (
              <span className="saving-text">Saving...</span>
            )}
          </div>
        </div>
      </div>
      {hasCadenceV2 && (
        <div className="email-form-tags-row" style={{ marginTop: '12px' }}>
          <label className="email-form-tags-label">Tags</label>
          <div className="email-form-tags-input-wrapper">
            <input
              type="text"
              className="email-form-tags-input"
              placeholder="Add a tag..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag(tagInput);
                }
              }}
            />
            <Button type="action" onClick={() => handleAddTag(tagInput)} disabled={!tagInput.trim()}>
              Add
            </Button>
            {tagSuggestions.length > 0 && tagInput.length > 0 && (
              <div className="create-cadence-configure--tag-dropdown">
                {tagSuggestions
                  .filter((s) => !tags.map((t) => t.toLowerCase()).includes(s.toLowerCase()))
                  .map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="create-cadence-configure--tag-dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddTag(suggestion);
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
              </div>
            )}
          </div>
          {tags.length > 0 && (
            <div className="email-form-tags-list">
              {tags.map((tag) => (
                <span key={tag} className="email-form-tag">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag)}>
                    <Icon icon="close" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

EmailSignaturesForm.propTypes = {
  selectedSignature: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    body: PropTypes.string,
  }).isRequired,
  updateSignature: PropTypes.func.isRequired,
  isSaved: PropTypes.bool.isRequired,
  setIsSaved: PropTypes.func.isRequired,
  contact: PropTypes.object,
};

export default EmailSignaturesForm;
